import {ref, computed} from 'vue'
import {useStore} from 'shared/stores/store'
import {useWorkspaceSubscriptionStore} from 'shared/stores/workspaceSubscription'
import {api} from 'shared/plugins/api'
import {useNotifyStore} from 'shared/stores/notify'

export const useWorkspaceInvites = () => {
	const resendingInvitesTo = ref([])
	const submittingForm = ref(false)

	const store = useStore()
	const workspaceSubscription = useWorkspaceSubscriptionStore()
	const notify = useNotifyStore()

	const defaultRole = computed(() => {
		if (store.workspace.isBrowserstackWorkspace) return 'full-member'
		const allowBugReporter = workspaceSubscription.allowedRoles.includes('bug-reporter')
		const allowMember = workspaceSubscription.allowedRoles.includes('full-member')

		if (!allowBugReporter && !allowMember) {
			return 'admin'
		}
		// in custom plans, if the member limit is reached, we still allow
		// invitation of bug-reporters
		if (!store.workspace.canInviteFullMember) {
			return 'bug-reporter'
		}
		return 'full-member'
	})

	const memberAndAdminUsersAndInvitesCount = computed(() => {
		if (!store.invites.length) return store.memberAndAdminUsersCount
		return (
			store.memberAndAdminUsersCount +
			store.invites.filter(entry => ['full-member', 'admin'].includes(entry.role)).length
		)
	})

	const existingInvitedEmails = computed(() => {
		return store.invites.map(invite => invite.email)
	})

	const existingMembersEmails = computed(() => {
		return store.users?.map(user => user.email) || []
	})

	function validateEmailForInvite({email, role, validity}) {
		email = email.trim()
		if (!email) {
			return {errorCode: 'isEmpty', message: 'Please enter an email address'}
		}
		if (validity && !validity.valid && (validity.typeMismatch || validity.patternMismatch)) {
			return {errorCode: 'isInvalid', message: 'Please enter a valid email address'}
		}
		if (existingMembersEmails.value.includes(email)) {
			return {errorCode: 'isMember', message: 'This email address is already a member of the workspace'}
		}
		if (existingInvitedEmails.value.includes(email)) {
			return {errorCode: 'isInvited', message: 'This email address is already a invited to this workspace'}
		}
		if (!store.workspace.canInviteFullMember && role !== 'bug-reporter') {
			return {
				errorCode: 'isLimitReached',
				message: workspaceSubscription.plan.perSeat
					? `Seat limit reached. Please ${
							store.hasUserAdminRole ? '' : 'ask an admin to '
					  }add more seats in the Subscription Plan settings.`
					: `Seat limit reached. Please ${
							store.hasUserAdminRole ? '' : 'ask an admin to '
					  }upgrade your plan in the Subscription Plan settings.`,
			}
		}
		return null
	}

	async function sendInvites(invites, resend = false, showNotification = true) {
		// remove extraneous properties that might have come with the invite
		// like 'borderColor'
		const invitedEmails = []
		const invitesToSend = invites.map(({role, email}) => {
			email = email.replace(/^\W+/, '').replace(/\W+$/, '')
			invitedEmails.push(email)
			return {email, role}
		})
		if (!invitesToSend.length) return
		try {
			if (!resend) submittingForm.value = true
			if (resend) resendingInvitesTo.value.push(...invitedEmails)

			await api.put('/invites/workspaces', {
				workspaceId: store.workspace.id,
				invites: invitesToSend,
			})
			await store.queries.currentWorkspace.refetch()
			if (showNotification) {
				notify.success({
					title: `Invite sent`,
					message: `Invite sent to ${invitedEmails.join(', ')}`,
				})
			}
		} catch (error) {
			error.notification = {
				title: `Invite(s) not sent.`,
				message: error.response?.message || 'Please try again.',
			}
			throw error
		} finally {
			if (!resend) {
				submittingForm.value = false
			} else if (resend) {
				resendingInvitesTo.value = resendingInvitesTo.value.filter(entry => !invitedEmails.includes(entry))
			}
		}
	}

	return {
		resendingInvitesTo,
		submittingForm,
		defaultRole,
		memberAndAdminUsersAndInvitesCount,
		existingInvitedEmails,
		existingMembersEmails,
		validateEmailForInvite,
		sendInvites,
	}
}
